// DOM-free core of the live OGS-streaming pipeline (STREAM_PIPELINE.md).
//
// This is the reusable DOM-free engine: WebSocket + decode worker ingest, the
// bounded decode→render jitter buffer, vertstride lerp, and the per-frame
// untile→lift→SH apply. It owns NO canvas, camera, controls, or DOM — the host
// supplies a THREE renderer + drives `update(cameraPos)` from its own render
// loop, and adds `generators` to its own scene.
//
// Host: StreamAvatarModel.jsx — the in-world R3F avatar (shared scene
// SparkRenderer), mounted from App.jsx (Switch Character).
//
// The server runs the CNN/SH inference; the browser only lifts UV→3D Gaussians
// and re-evaluates SH color view-dependently each render frame. NO in-browser CNN.

import * as THREE from "three";
import { StreamLiftGenerator } from "../render/spark/StreamLiftGenerator";
import { ThreeUntile } from "../render/spark/ThreeUntile";
import { loadStreamScene, type MeshAssets } from "../assets/streamScene";
import type { LiftDims } from "../render/webgl/liftTypes";

export interface StreamStats {
  renderFps: number;
  decodeFps: number;
  branchLabel: string;
  degree: number;
  queueLen: number;
}

export interface StreamControllerOpts {
  /** THREE renderer the untile + lift passes run on (the host's, NOT a private one). */
  renderer: THREE.WebGLRenderer;
  /** Fully-built WS URL (already carries ?character/bitrate/vertstride). */
  wsUrl: string;
  /** Resolve the scene (scene.json URL) the client lifts against, given the
   *  server meta. The mesh topology MUST match the streamed character. The URL
   *  is the character's server-side `/ogs` scene row. */
  resolveSceneUrl: (meta: any) => string;
  /** Build-progress line from the server, sent while it loads a character that was
   *  not resident. Drives the host's loading overlay — a cold TensorRT compile is
   *  minutes, and an unannounced wait is indistinguishable from a hang. */
  onStage?: (stage: string) => void;
  /** Map a scene sub-asset filename → its server URL (per-file AAM rows).
   *  Required — streaming assets are server-sourced only, no bundled fallback. */
  resolveAssetUrl: (filename: string) => string;
  /** Fetch one scene asset. Omit for plain `fetch`; the host passes
   *  `cachedFetchAamFile` so the stream shares the on-device /ogs path's URL
   *  policy + Cache Storage instead of re-downloading meshes every load. */
  fetchAsset?: (url: string, size?: number) => Promise<Response>;
  /** Fetch the scene.json descriptor. Kept separate from `fetchAsset` so the host
   *  can leave it UNCACHED — a same-size descriptor edit would slip past the
   *  cache's size-versioned key and shadow the server. */
  fetchDescriptor?: (url: string, size?: number) => Promise<Response>;
  /** Byte size by filename, for the cache's size-versioned key. */
  sizeOf?: (filename: string) => number | undefined;
  /** decode→render FIFO depth; 0 = latest-wins oracle (no playout). Default 3. */
  queueDepth?: number;
  /** send head verts every Nth frame, client lerps the gaps. Default 1. */
  vertStride?: number;
  onStatus?: (m: string) => void;
  onStats?: (s: StreamStats) => void;
}

export interface StreamController {
  /** Per-stream metadata from the server (fps, look_at, radius, fov, rig_*…). */
  meta: any;
  /** The resolved scene URL the lift runs against (so the host can locate rig_names.json). */
  sceneUrl: string;
  /** True when the server runs live inference and accepts rig updates. */
  rigDriven: boolean;
  /** Splat objects to add to the host scene: [head] or [head, body]. */
  generators: THREE.Object3D[];
  /** Send a head control-rig to the server (live inference). No-op if not rig-driven. */
  sendRig(rig: ArrayLike<number>): void;
  /** Call once per host render frame with the live camera world position. Drains the
   *  decode→render queue (untile+lift on a new frame) and re-shades SH from the camera. */
  update(cameraPos: THREE.Vector3): void;
  dispose(): void;
}

interface QItem { frameIdx: number; bitmap: ImageBitmap | null; head: Float32Array | null; interp?: boolean }

const dimsOf = (m: MeshAssets): LiftDims => {
  const H = m.manifest.uv_res;
  return { H, W: H, HW: H * H, N: H, F: m.manifest.num_faces, V: m.manifest.num_vertices };
};

export async function createStreamController(opts: StreamControllerOpts): Promise<StreamController> {
  if (!("VideoDecoder" in window)) throw new Error("WebCodecs VideoDecoder not available");
  const { renderer, wsUrl } = opts;
  const log = (m: string) => { opts.onStatus?.(m); console.log("[stream]", m); };

  // ---- bounded decode→render FIFO jitter buffer (worker fills, host drains in order) ----
  const QDEPTH = Number.isFinite(opts.queueDepth) ? (opts.queueDepth as number) : 3;
  const useQueue = QDEPTH > 0;
  const frameQ: QItem[] = [];
  let latest: QItem | null = null, decodedCount = 0, lastHead: Float32Array | null = null, bodyVerts: Float32Array | null = null;
  const vertsMap = new Map<number, { head: Float32Array }>();
  const VERTS_CAP = 90;
  const pruneVerts = () => { while (vertsMap.size > VERTS_CAP) vertsMap.delete(vertsMap.keys().next().value as number); };

  let stopped = false;
  const worker = new Worker(new URL("./decodeWorker.ts", import.meta.url), { type: "module" });

  log(`connecting to ${wsUrl}…`);
  const meta: any = await new Promise((resolve, reject) => {
    worker.onmessage = (e: MessageEvent) => {
      const m: any = e.data;
      if (m.type === "meta") { resolve(m.meta); return; }
      if (m.type === "frame") {
        decodedCount++;
        if (useQueue) {
          const hasOwn = vertsMap.has(m.frameIdx);
          const head = hasOwn ? vertsMap.get(m.frameIdx)!.head : (m.interp ? null : lastHead);
          const newPose = hasOwn || m.interp;
          if (!newPose && frameQ.length) {
            const tail = frameQ[frameQ.length - 1];
            if (tail.bitmap) tail.bitmap.close();
            tail.bitmap = m.bitmap; tail.frameIdx = m.frameIdx;
            worker.postMessage({ type: "consumed" });
          } else {
            frameQ.push({ frameIdx: m.frameIdx, bitmap: m.bitmap, head, interp: m.interp && !hasOwn });
            while (frameQ.length > QDEPTH + 1) { const old = frameQ.shift()!; if (old.bitmap) old.bitmap.close(); worker.postMessage({ type: "consumed" }); }
          }
        } else {
          if (latest && latest.bitmap) latest.bitmap.close();
          latest = { frameIdx: m.frameIdx, bitmap: m.bitmap, head: null };
          worker.postMessage({ type: "consumed" });
        }
      } else if (m.type === "headVerts") { vertsMap.set(m.idx, { head: m.head }); lastHead = m.head; pruneVerts(); }
      else if (m.type === "bodyVerts") { bodyVerts = m.verts; }
      else if (m.type === "ready") console.log(`[worker] decoder ready hw=${m.hw}`);
      else if (m.type === "status") { log(`server: ${m.stage}`); opts.onStage?.(m.stage); }
      else if (m.type === "error") log("worker: " + m.msg);
      else if (m.type === "reconnecting") log(`reconnecting in ${m.delayMs}ms…`);
      else if (m.type === "closed") {
        log(`stream closed by server${m.code ? ` (${m.code}${m.reason ? `: ${m.reason}` : ""})` : ""}.`);
        // Refused outright — surface it. Before meta this rejects the init promise and
        // reaches the App error overlay; after, the reject is a no-op and the log stands.
        if (m.code === 1008) reject(new Error(m.reason || "the stream server refused this character"));
        // 1013 means "not yet, keep trying" and the worker does. Show the server's
        // reason while it does: a build that is failing and being retried otherwise
        // looks exactly like one that is slow, which is the spinner-forever failure.
        else if (m.code === 1013 && m.reason) opts.onStage?.(m.reason);
      }
    };
    worker.onerror = (e) => reject(new Error("decode worker failed: " + e.message));
    worker.postMessage({ type: "init", wsUrl, cap: useQueue ? QDEPTH : 2 });
  });
  if (stopped) { worker.terminate(); return stubController(meta); }
  // Past startup the meta promise is settled, so a worker crash can no longer reject
  // it — surface the error instead of silently dropping the stream.
  worker.onerror = (e) => log("worker error: " + (e?.message ?? e));

  const K: number = meta.K;
  log(`meta: ${meta.nframes} frames @ ${meta.fps} fps, ${meta.w}x${meta.h}, SH deg${meta.degree} (K=${K}). starting…`);

  // ---- rig: send a head control-rig to the server (live inference) ----
  const rigDriven = !!meta.rig_driven;
  const sendRig = (rig: ArrayLike<number>) => {
    if (!rigDriven || stopped) return;
    const r = rig instanceof Float32Array ? rig : Float32Array.from(rig as number[]);
    worker.postMessage({ type: "sendRig", rig: r });
  };

  // ---- GPU lift setup (on the HOST renderer) ----
  // loadStreamScene (and the asset resolver) throw loudly when a server asset is
  // missing — terminate the still-running worker (WebSocket + VideoDecoder) on any
  // setup failure so a failed init can't leak a live, reconnecting stream.
  let untiler: ThreeUntile, sgHead: StreamLiftGenerator, sgBody: StreamLiftGenerator | null;
  let headMesh: any, bodyMesh: any, branchLabel: string;
  let sceneUrl: string;
  try {
    sceneUrl = opts.resolveSceneUrl(meta);
    const scene = await loadStreamScene(sceneUrl, opts.resolveAssetUrl,
      { fetchAsset: opts.fetchAsset, fetchDescriptor: opts.fetchDescriptor, sizeOf: opts.sizeOf });
    // Branch-generic: the primary (rig-driven) branch is always "head" — for a
    // single_region avatar (Isaac) that's the ONLY branch; multi_region (Myra) adds
    // the baked secondary "body".
    headMesh = scene.branches.head.manifest;
    bodyMesh = scene.branches.body?.manifest ?? null;
    branchLabel = scene.order.join("+");   // "head" | "head+body"

    untiler = new ThreeUntile(renderer, meta);
    // gpuJac=true: pass1 computes the per-face Jacobian inline (drops the CPU jac upload).
    sgHead = new StreamLiftGenerator(renderer, headMesh, dimsOf(headMesh), untiler, K, "head", true);
    sgBody = bodyMesh ? new StreamLiftGenerator(renderer, bodyMesh, dimsOf(bodyMesh), untiler, K, "body", true) : null;
  } catch (err) {
    stopped = true;
    worker.terminate();
    throw err;
  }
  const generators: THREE.Object3D[] = [sgHead.generator as unknown as THREE.Object3D];
  if (sgBody) generators.push(sgBody.generator as unknown as THREE.Object3D);

  let nRender = 0, bodyPosed = false, lastBodyPosed: Float32Array | null = null, lastFedIdx = -1;
  const bitmapTex = new THREE.Texture();
  bitmapTex.flipY = false; bitmapTex.minFilter = THREE.NearestFilter; bitmapTex.magFilter = THREE.NearestFilter;
  let feeding = false, pendingFrame: { bitmap: ImageBitmap; head: Float32Array } | null = null;

  // ?vertstride=N: server omits head verts on gap frames; reconstruct each gap by
  // linearly interpolating the two bracketing keyframes' verts.
  const lerpHead = new Float32Array(meta.verts_head * 3);
  const resolveHead = (fi: number): Float32Array | null => {
    let kA = -1, kB = -1;
    for (const k of vertsMap.keys()) {
      if (k <= fi) { if (kA < 0 || k > kA) kA = k; }
      else { if (kB < 0 || k < kB) kB = k; }
    }
    if (kA < 0) return lastHead;
    const A = vertsMap.get(kA)!.head;
    if (kB < 0) return A;
    const B = vertsMap.get(kB)!.head;
    const f = (fi - kA) / (kB - kA);
    if (f <= 0) return A; if (f >= 1) return B;
    for (let i = 0; i < A.length; i++) lerpHead[i] = A[i] + (B[i] - A[i]) * f;
    return lerpHead;
  };

  const feedOnce = () => {
    if (feeding || !pendingFrame) return; feeding = true;
    try {
      while (pendingFrame) {
        const { bitmap, head } = pendingFrame; pendingFrame = null;
        bitmapTex.image = bitmap; bitmapTex.needsUpdate = true;
        untiler.setAtlas(bitmapTex); untiler.untile("expanded");
        bitmap.close();
        sgHead.setPose(head); sgHead.runLift();
        if (sgBody) {
          if (bodyVerts && bodyVerts !== lastBodyPosed) { sgBody.setPose(bodyVerts); lastBodyPosed = bodyVerts; bodyPosed = true; }
          if (bodyPosed) sgBody.runLift();
        }
        nRender++;
      }
    } catch (e) { console.warn("[stream] feed", e); } finally { feeding = false; }
  };

  // ---- paced playout buffer: prebuffer a few frames, present one per PLAY_PERIOD ----
  const PLAY_PERIOD = 1000 / (meta.fps || 30);
  const vertStride = Math.max(opts.vertStride ?? 1, 1);
  const PREBUFFER = Math.min(QDEPTH, Math.max(3, vertStride + 1));
  const PRIME_MS = 250;
  let playStarted = false, nextPlay = 0, primeT0 = 0;
  let lastF = performance.now(), fren = nRender, fdec = decodedCount;

  const present = (item: QItem) => {
    const head = item.head ?? (item.interp ? resolveHead(item.frameIdx) : lastHead);
    if (head) {
      lastFedIdx = item.frameIdx;
      pendingFrame = { bitmap: item.bitmap!, head };
      worker.postMessage({ type: "consumed" });
      feedOnce();
      return true;
    }
    if (item.bitmap) item.bitmap.close();
    worker.postMessage({ type: "consumed" });
    return false;
  };

  const update = (cameraPos: THREE.Vector3) => {
    if (stopped) return;
    // Re-shade SH every frame from the live camera (camera-paced, zero inference).
    sgHead.setCamera(cameraPos); sgBody?.setCamera(cameraPos);

    if (useQueue) {
      const tnow = performance.now();
      if (!playStarted) {
        if (frameQ.length >= PREBUFFER) { playStarted = true; nextPlay = tnow; }
        else if (frameQ.length >= 1) { if (!primeT0) primeT0 = tnow; else if (tnow - primeT0 > PRIME_MS) { playStarted = true; nextPlay = tnow; } }
      }
      if (playStarted && tnow >= nextPlay && !pendingFrame && !feeding) {
        const item = frameQ.shift();
        if (item) {
          present(item);
          nextPlay += PLAY_PERIOD;
          if (tnow - nextPlay > PLAY_PERIOD) nextPlay = tnow + PLAY_PERIOD;
        }
      }
    } else if (latest && latest.frameIdx !== lastFedIdx && latest.bitmap) {
      const vmap = vertsMap.get(latest.frameIdx);
      const head = vmap ? vmap.head : lastHead;
      if (head) {
        lastFedIdx = latest.frameIdx;
        pendingFrame = { bitmap: latest.bitmap, head };
        latest = { frameIdx: latest.frameIdx, bitmap: null, head: null };
        feedOnce();
      }
    }

    const now = performance.now();
    if (opts.onStats && now - lastF > 500) {
      const dt = (now - lastF) / 1000;
      opts.onStats({
        renderFps: (nRender - fren) / dt,
        decodeFps: (decodedCount - fdec) / dt,
        branchLabel, degree: meta.degree, queueLen: frameQ.length,
      });
      lastF = now; fren = nRender; fdec = decodedCount;
    }
  };
  log(`LIVE SH WebGL (${branchLabel}) deg${meta.degree}. Orbit for view-dependent color.`);

  // ---- ready-trigger: send rig0 once we're actually rendering → server converges ----
  if (rigDriven) {
    const rig0c = meta.rig0_head || new Array(meta.rig_dim_head).fill(0);
    let _cv = false, _tries = 0;
    const reqConverge = () => {
      if (_cv || stopped) return;
      if (lastFedIdx > -1 || ++_tries > 25) { _cv = true; sendRig(rig0c); }
      else setTimeout(reqConverge, 120);
    };
    setTimeout(reqConverge, 120);
  }

  return {
    meta, sceneUrl, rigDriven, generators, sendRig, update,
    dispose() {
      stopped = true;
      // Ask the worker to close its WebSocket + VideoDecoder gracefully (its "stop"
      // handler self-terminates via close()); terminate() below is a safety net that
      // fires only if the worker is wedged and never processes the message.
      try { worker.postMessage({ type: "stop" }); } catch { /* worker may be down */ }
      setTimeout(() => worker.terminate(), 1500);
      try { sgHead.dispose(); sgBody?.dispose(); untiler.dispose(); } catch { /* best effort */ }
      bitmapTex.dispose();
      for (const it of frameQ) it.bitmap?.close();
      frameQ.length = 0;
      // In-flight frames the queue drain handed off but didn't yet feed.
      pendingFrame?.bitmap.close(); pendingFrame = null;
      latest?.bitmap?.close(); latest = null;
    },
  };
}

/** Minimal no-op controller returned when the host disposed before meta arrived. */
function stubController(meta: any): StreamController {
  return { meta, sceneUrl: "", rigDriven: false, generators: [], sendRig() {}, update() {}, dispose() {} };
}
